import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import axios from 'axios'
import { useState, useEffect } from 'react'

import { axiosInstance } from '../../../helpers'
import {
    PaginatedResponse,
    LeaseDocument,
    ResidentTerm,
    PresignedUrlResponse,
    UploadProgress,
    UploadCompleteRequest,
    UploadCompleteResponse,
    MultipartInitiateResponse,
    PartPresignedUrlResponse,
    MultipartPart,
    DocumentUploadRecord,
    TaskProgressResponse,
    DocumentProcessingStatusFilter,
} from '../types'

export interface UseDocumentsOptions {
    auditId: number
    page?: number
    pageSize?: number
    search?: string
    paired?: boolean | null
    processingStatus?: DocumentProcessingStatusFilter
    enabled?: boolean
    parsingTaskId?: string | null
    extractionTaskId?: string | null
}

export interface PairDocumentParams {
    documentId: number
    residentTermId: number
}

export interface UnpairDocumentParams {
    documentId: number
}

export interface SelectDocumentParams {
    documentId: number
}

export interface EditDocumentParams {
    documentId: number
    updates: {
        resident_name?: string
        lease_start?: string
        lease_end?: string
    }
}

export interface ExtractDocumentsParams {
    documentIds: number[]
}

export interface SearchDocumentsParams {
    residentTermId: number
}

// Constants for retry logic and multipart upload
const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY = 1000 // 1 second
const MAX_FILE_SIZE = 20 * 1024 * 1024 * 1024 // 20GB
const MULTIPART_THRESHOLD = 1 * 1024 * 1024 * 1024 // 1GB - use multipart for files larger than this
const CHUNK_SIZE = 100 * 1024 * 1024 // 100MB per part
const PART_UPLOAD_TIMEOUT = 10 * 60 * 1000 // 10 minutes per part

// Helper function for exponential backoff retry
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const retryWithBackoff = async <T>(
    fn: () => Promise<T>,
    retries: number = MAX_RETRIES,
    delay: number = INITIAL_RETRY_DELAY,
): Promise<T> => {
    try {
        return await fn()
    } catch (error) {
        if (retries === 0) {
            throw error
        }
        await sleep(delay)
        return retryWithBackoff(fn, retries - 1, delay * 2)
    }
}

export const useDocuments = (options: UseDocumentsOptions) => {
    const {
        auditId,
        page = 1,
        pageSize = 25,
        search = '',
        paired = null,
        processingStatus = null,
        enabled = true,
        parsingTaskId = null,
        extractionTaskId = null,
    } = options
    const queryClient = useQueryClient()

    // Upload progress state
    const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
        null,
    )
    const [isUploading, setIsUploading] = useState(false)
    const [uploadError, setUploadError] = useState<string | null>(null)

    // Fetch documents
    const {
        data: documentsData,
        isLoading,
        isFetching,
        dataUpdatedAt,
        error,
        refetch,
    } = useQuery<
        AxiosResponse<PaginatedResponse<LeaseDocument> | LeaseDocument[]>,
        Error
    >({
        queryKey: [
            'documents',
            auditId,
            page,
            pageSize,
            search,
            paired,
            processingStatus,
        ],
        queryFn: () => {
            const params = new URLSearchParams()
            params.append('page', page.toString())
            params.append('page_size', pageSize.toString())
            if (search) {
                params.append('search', search)
            }
            if (paired !== null) {
                params.append('paired', paired.toString())
            }
            if (processingStatus) {
                params.append('processing_status', processingStatus)
            }

            return axiosInstance.get(
                `/lease-audit/${auditId}/documents/?${params.toString()}`,
            )
        },
        enabled: enabled && !!auditId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const beginParseMutation = useMutation({
        mutationFn: () =>
            axiosInstance.post(`/lease-audit/${auditId}/documents/parse/`),
        onSuccess: (response: AxiosResponse) => {
            if (response.status === 202) {
                queryClient.invalidateQueries({
                    queryKey: ['leaseAuditDetail', auditId],
                })
            }
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        },
    })
    const beginExtractionMutation = useMutation({
        mutationFn: () =>
            axiosInstance.post(
                `/lease-audit/${auditId}/documents/extract-data/`,
            ),
        onSuccess: (response: AxiosResponse) => {
            if (response.status === 202) {
                queryClient.invalidateQueries({
                    queryKey: ['leaseAuditDetail', auditId],
                })
            }
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        },
    })

    // Poll for parsing task status
    const {
        data: parsingTaskStatus,
        isLoading: isLoadingParsingTask,
        error: parsingTaskError,
    } = useQuery<AxiosResponse<TaskProgressResponse>, Error>({
        queryKey: ['documentParsingTask', parsingTaskId],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/documents/parsing/task-status/${parsingTaskId}/`,
            ),
        enabled: Boolean(parsingTaskId),
        refetchInterval: (query) => {
            // Stop polling if status is completed or failed
            if (!query) return false
            const latestData = (query as any).state?.data as
                | AxiosResponse<TaskProgressResponse>
                | undefined
            if (
                latestData?.data?.task_status === 'SUCCESS' ||
                latestData?.data?.task_status === 'FAILED'
            ) {
                return false
            }
            // Poll every 2 seconds while pending or processing
            return 2000
        },
    })

    const parsingTaskFailureMessage =
        parsingTaskStatus?.data?.sub_tasks
            ?.map((subTask) => subTask.results.sub_task_error)
            .find((errorMessage) => !!errorMessage) ?? null
    const parsingTaskHasFailed =
        parsingTaskStatus?.data?.task_status === 'FAILED'

    // When parsing completes, invalidate the lease audit detail query
    // this will set the parsing task ID to null and disable the polling
    useEffect(() => {
        if (
            parsingTaskStatus?.data?.task_status === 'SUCCESS' ||
            parsingTaskStatus?.data?.task_status === 'FAILED'
        ) {
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        }
    }, [parsingTaskStatus?.data?.task_status, queryClient, auditId])

    // Poll for extraction task status
    const {
        data: extractionTaskStatus,
        isLoading: isLoadingExtractionTask,
        error: extractionTaskError,
    } = useQuery<AxiosResponse<TaskProgressResponse>, Error>({
        queryKey: ['documentExtractionTask', extractionTaskId],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/documents/extracting/task-status/${extractionTaskId}/`,
            ),
        enabled: Boolean(extractionTaskId),
        refetchInterval: (query) => {
            // Stop polling if status is completed or failed
            if (!query) return false
            const latestData = (query as any).state?.data as
                | AxiosResponse<TaskProgressResponse>
                | undefined
            if (
                latestData?.data?.task_status === 'SUCCESS' ||
                latestData?.data?.task_status === 'FAILED'
            ) {
                return false
            }
            // Poll every 2 seconds while pending or processing
            return 2000
        },
    })

    const extractionTaskFailureMessage =
        extractionTaskStatus?.data?.sub_tasks
            ?.map((subTask) => subTask.results.sub_task_error)
            .find((errorMessage) => !!errorMessage) ?? null
    const extractionTaskHasFailed =
        extractionTaskStatus?.data?.task_status === 'FAILED'

    // When extraction completes, invalidate the lease audit detail query
    // this will set the extraction task ID to null and disable the polling
    useEffect(() => {
        if (
            extractionTaskStatus?.data?.task_status === 'SUCCESS' ||
            extractionTaskStatus?.data?.task_status === 'FAILED'
        ) {
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        }
    }, [extractionTaskStatus?.data?.task_status, queryClient, auditId])

    // Fetch upload history
    const {
        data: uploadHistoryData,
        isLoading: isLoadingUploadHistory,
    } = useQuery<AxiosResponse<DocumentUploadRecord[]>, Error>({
        queryKey: ['documentUploadHistory', auditId],
        queryFn: () =>
            axiosInstance.get(
                `/lease-audit/${auditId}/documents-upload/history/`,
            ),
        enabled: enabled && !!auditId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Upload documents with multipart upload for large files
    const uploadDocumentsMultipart = async (file: File) => {
        let multipartUploadId: string | null = null
        let upload_id: number | null = null

        try {
            // Phase 1: Initiate multipart upload
            const initiateResponse = await retryWithBackoff(async () => {
                try {
                    return await axiosInstance.post<MultipartInitiateResponse>(
                        `/lease-audit/${auditId}/documents-upload/initiate-multipart/`,
                        {
                            file_name: file.name,
                            file_size: file.size,
                            content_type: file.type || 'application/zip',
                        },
                    )
                } catch (error: any) {
                    const message =
                        error.response?.data?.message || error.message
                    throw new Error(
                        `Failed to initiate multipart upload: ${message}`,
                    )
                }
            })

            const { multipart_upload_id, s3_key } = initiateResponse.data
            multipartUploadId = multipart_upload_id
            upload_id = initiateResponse.data.upload_id

            // Phase 2: Upload parts
            const totalParts = Math.ceil(file.size / CHUNK_SIZE)
            const parts: MultipartPart[] = []
            const overallStartTime = Date.now()

            for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
                const start = (partNumber - 1) * CHUNK_SIZE
                const end = Math.min(start + CHUNK_SIZE, file.size)
                const chunk = file.slice(start, end)

                // Get presigned URL for this part
                const partUrlResponse = await retryWithBackoff(async () => {
                    try {
                        return await axiosInstance.post<PartPresignedUrlResponse>(
                            `/lease-audit/${auditId}/documents-upload/part-presigned-url/`,
                            {
                                upload_id,
                                multipart_upload_id: multipartUploadId,
                                part_number: partNumber,
                            },
                        )
                    } catch (error: any) {
                        const message =
                            error.response?.data?.message || error.message
                        throw new Error(
                            `Failed to get presigned URL for part ${partNumber}: ${message}`,
                        )
                    }
                })

                // Upload this part with retry
                const uploadResponse = await retryWithBackoff(async () => {
                    return await axios.put(
                        partUrlResponse.data.presigned_url,
                        chunk,
                        {
                            headers: {
                                'Content-Type': 'application/zip',
                            },
                            timeout: PART_UPLOAD_TIMEOUT,
                            onUploadProgress: (progressEvent) => {
                                // Calculate overall progress across all parts
                                const partProgress = progressEvent.loaded
                                const previousPartsBytes =
                                    (partNumber - 1) * CHUNK_SIZE
                                const overallProgress =
                                    previousPartsBytes + partProgress
                                const percentage = Math.round(
                                    (overallProgress / file.size) * 100,
                                )

                                const elapsedTime =
                                    (Date.now() - overallStartTime) / 1000
                                const speed = overallProgress / elapsedTime
                                const remainingBytes =
                                    file.size - overallProgress
                                const eta =
                                    speed > 0 ? remainingBytes / speed : 0

                                setUploadProgress({
                                    percentage,
                                    bytesUploaded: overallProgress,
                                    totalBytes: file.size,
                                    speed,
                                    eta,
                                    startTime: overallStartTime,
                                })
                            },
                        },
                    )
                })

                // Extract ETag from response headers (remove quotes)
                // Try both lowercase and uppercase for header case sensitivity
                const etag =
                    (
                        uploadResponse.headers['etag'] ||
                        uploadResponse.headers['ETag']
                    )?.replace(/"/g, '') || ''
                if (!etag) {
                    throw new Error(`Failed to get ETag for part ${partNumber}`)
                }
                parts.push({ part_number: partNumber, etag })
            }

            // Phase 3: Complete multipart upload
            const completeResponse = await retryWithBackoff(async () => {
                try {
                    return await axiosInstance.post<UploadCompleteResponse>(
                        `/lease-audit/${auditId}/documents-upload/complete-multipart/`,
                        {
                            upload_id,
                            multipart_upload_id: multipartUploadId,
                            parts,
                        },
                    )
                } catch (error: any) {
                    const message =
                        error.response?.data?.message || error.message
                    throw new Error(
                        `Failed to complete multipart upload: ${message}`,
                    )
                }
            })

            // Invalidate queries to refresh data
            queryClient.invalidateQueries({
                queryKey: ['documents', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['residentTerms', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['documentUploadHistory', auditId],
            })

            setUploadProgress(null)
            setIsUploading(false)
        } catch (error: any) {
            // Abort multipart upload to prevent orphaned parts in S3
            if (multipartUploadId && upload_id) {
                await axiosInstance
                    .post(
                        `/lease-audit/${auditId}/documents-upload/abort-multipart/`,
                        {
                            upload_id,
                            multipart_upload_id: multipartUploadId,
                        },
                    )
                    .catch(() => {
                        // Ignore abort errors
                    })
            }

            const errorMessage = error.message || 'Failed to upload file'
            setUploadError(errorMessage)
            setUploadProgress(null)
            setIsUploading(false)
            throw error
        }
    }

    // Upload documents with single PUT (for files <= 1GB)
    const uploadDocumentsSinglePut = async (file: File) => {
        try {
            // Phase 1: Request presigned URL (with retry)
            const presignedResponse = await retryWithBackoff(async () => {
                try {
                    return await axiosInstance.post<PresignedUrlResponse>(
                        `/lease-audit/${auditId}/documents-upload/presigned-url/`,
                        {
                            file_name: file.name,
                            file_size: file.size,
                            content_type: file.type || 'application/zip',
                        },
                    )
                } catch (error: any) {
                    const message =
                        error.response?.data?.message || error.message
                    throw new Error(`Failed to request upload URL: ${message}`)
                }
            })

            // Validate presigned URL response
            if (!presignedResponse.data) {
                throw new Error(
                    'Invalid response from server: no data received',
                )
            }

            const { presigned_url, s3_key, upload_id } = presignedResponse.data

            if (!presigned_url) {
                console.error('Presigned response:', presignedResponse.data)
                throw new Error(
                    'Invalid response from server: missing presigned_url. Please contact support.',
                )
            }

            if (!s3_key) {
                console.error('Presigned response:', presignedResponse.data)
                throw new Error(
                    'Invalid response from server: missing s3_key. Please contact support.',
                )
            }

            if (!upload_id) {
                console.error('Presigned response:', presignedResponse.data)
                throw new Error(
                    'Invalid response from server: missing upload_id. Please contact support.',
                )
            }

            // Phase 2: Upload to S3 with progress tracking
            const startTime = Date.now()
            try {
                await axios.put(presigned_url, file, {
                    headers: {
                        'Content-Type': file.type || 'application/zip',
                    },
                    timeout: 30 * 60 * 1000, // 30 minutes timeout
                    maxBodyLength: Infinity,
                    maxContentLength: Infinity,
                    onUploadProgress: (progressEvent) => {
                        const bytesUploaded = progressEvent.loaded
                        const totalBytes = progressEvent.total || file.size
                        const percentage = Math.round(
                            (bytesUploaded / totalBytes) * 100,
                        )

                        const elapsedTime = (Date.now() - startTime) / 1000 // seconds
                        const speed = bytesUploaded / elapsedTime // bytes per second
                        const remainingBytes = totalBytes - bytesUploaded
                        const eta = speed > 0 ? remainingBytes / speed : 0

                        setUploadProgress({
                            percentage,
                            bytesUploaded,
                            totalBytes,
                            speed,
                            eta,
                            startTime,
                        })
                    },
                })
            } catch (error: any) {
                throw new Error(
                    `Failed to upload file to S3: ${error.message}. Please check your internet connection and try again.`,
                )
            }

            // Phase 3: Notify backend of completion (with retry)
            const uploadCompleteResponse = await retryWithBackoff(async () => {
                try {
                    return await axiosInstance.post<UploadCompleteResponse>(
                        `/lease-audit/${auditId}/documents-upload/upload-complete/`,
                        {
                            s3_key,
                            upload_id,
                        } as UploadCompleteRequest,
                    )
                } catch (error: any) {
                    const message =
                        error.response?.data?.message || error.message
                    throw new Error(`Failed to complete upload: ${message}`)
                }
            })

            // Invalidate queries to refresh data
            queryClient.invalidateQueries({
                queryKey: ['documents', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['residentTerms', auditId],
            })
            // Invalidate lease audit detail to pick up updated latest_ready_step
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['documentUploadHistory', auditId],
            })

            setUploadProgress(null)
            setIsUploading(false)
        } catch (error: any) {
            const errorMessage = error.message || 'Failed to upload file'
            setUploadError(errorMessage)
            setUploadProgress(null)
            setIsUploading(false)
            throw error
        }
    }

    // Main upload function that routes between single PUT and multipart based on file size
    const uploadDocuments = async (file: File) => {
        setIsUploading(true)
        setUploadError(null)
        setUploadProgress({
            percentage: 0,
            bytesUploaded: 0,
            totalBytes: file.size,
            speed: 0,
            eta: 0,
            startTime: Date.now(),
        })

        try {
            // Validate file type
            if (!file.name.toLowerCase().endsWith('.zip')) {
                throw new Error('Only ZIP files are accepted')
            }

            // Validate file size
            if (file.size === 0) {
                throw new Error('File is empty')
            }

            if (file.size > MAX_FILE_SIZE) {
                throw new Error(
                    `File size exceeds maximum allowed size of ${
                        MAX_FILE_SIZE / (1024 * 1024 * 1024)
                    }GB`,
                )
            }

            // Route to appropriate upload method based on file size
            if (file.size > MULTIPART_THRESHOLD) {
                // Use multipart upload for files > 1GB
                await uploadDocumentsMultipart(file)
            } else {
                // Use single PUT for smaller files
                await uploadDocumentsSinglePut(file)
            }
        } catch (error: any) {
            // Handle validation errors (before upload functions are called)
            // Upload functions handle their own errors and set state
            if (!uploadError) {
                // Only set error if upload functions haven't already
                const errorMessage = error.message || 'Failed to upload file'
                setUploadError(errorMessage)
                setUploadProgress(null)
                setIsUploading(false)
            }
            throw error
        }
    }

    // Pair document to resident term
    const pairDocumentMutation = useMutation({
        mutationFn: (params: PairDocumentParams) =>
            axiosInstance.post(
                `/lease-audit/${auditId}/documents/pair-document/`,
                {
                    resident_term_id: params.residentTermId,
                    document_id: params.documentId,
                },
            ),
    })

    // Unpair document from resident term
    const unpairDocumentMutation = useMutation({
        mutationFn: (params: UnpairDocumentParams) =>
            axiosInstance.post(
                `/lease-audit/${auditId}/documents/unpair-document/`,
                {
                    document_id: params.documentId,
                },
            ),
    })

    // Select/deselect document for extraction
    const selectDocumentMutation = useMutation({
        mutationFn: (params: SelectDocumentParams) =>
            axiosInstance.post(
                `/lease-audit/${auditId}/documents/select-document/`,
                { document_id: params.documentId },
            ),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['documents', auditId],
            })
            queryClient.setQueriesData(
                { queryKey: ['residentTerms', auditId] },
                (
                    oldData:
                        | AxiosResponse<PaginatedResponse<ResidentTerm>>
                        | AxiosResponse<ResidentTerm[]>
                        | undefined,
                ) => {
                    if (!oldData) return oldData

                    const updateTerm = (term: ResidentTerm): ResidentTerm => {
                        if (!term.paired_documents) return term

                        const hasDocument = term.paired_documents.some(
                            (doc) => doc.id === variables.documentId,
                        )
                        if (!hasDocument) return term

                        return {
                            ...term,
                            has_selected_document: true,
                            paired_documents: term.paired_documents.map(
                                (doc) => ({
                                    ...doc,
                                    is_selected:
                                        doc.id === variables.documentId,
                                }),
                            ),
                        }
                    }

                    if (Array.isArray(oldData.data)) {
                        return {
                            ...oldData,
                            data: oldData.data.map(updateTerm),
                        }
                    }

                    if (oldData.data?.results) {
                        return {
                            ...oldData,
                            data: {
                                ...oldData.data,
                                results: oldData.data.results.map(updateTerm),
                            },
                        }
                    }

                    return oldData
                },
            )
        },
    })

    // Edit document parsed values
    const editDocumentMutation = useMutation({
        mutationFn: (params: EditDocumentParams) =>
            axiosInstance.patch(
                `/lease-audit/${auditId}/documents/${params.documentId}/`,
                params.updates,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['documents', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['residentTerms', auditId],
            })
        },
    })

    // Extract data from selected documents
    const extractDocumentsMutation = useMutation({
        mutationFn: (params: ExtractDocumentsParams) =>
            axiosInstance.post(
                `/lease-audit/${auditId}/documents/extract-data/`,
                {
                    document_ids: params.documentIds,
                },
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['documents', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['audit', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditDetail', auditId],
            })
        },
    })

    // Extract all selected documents
    const extractAllSelectedMutation = useMutation({
        mutationFn: () =>
            axiosInstance.post(
                `/lease-audit/${auditId}/documents/extract-all-selected/`,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['documents', auditId],
            })
            queryClient.invalidateQueries({
                queryKey: ['audit', auditId],
            })
        },
    })

    // Search for documents with fuzzy matching
    const searchDocumentsMutation = useMutation({
        mutationFn: (params: SearchDocumentsParams) =>
            axiosInstance.post<LeaseDocument[]>(
                `/lease-audit/${auditId}/documents/search-unpaired-documents/`,
                { resident_term_id: params.residentTermId },
            ),
    })

    const responseData = documentsData?.data
    const documents = Array.isArray(responseData)
        ? responseData
        : responseData?.results ?? []
    const totalCount = Array.isArray(responseData)
        ? responseData.length
        : responseData?.count ?? 0
    const uploadHistory = uploadHistoryData?.data ?? []

    return {
        // Data
        documents,
        totalCount,
        totalPages: Math.ceil(totalCount / pageSize),
        uploadHistory,

        // Loading states
        isLoading,
        isUploading,
        isLoadingUploadHistory,
        isProcessingExtraction: !!extractionTaskId,
        isPairing: pairDocumentMutation.isLoading,
        isUnpairing: unpairDocumentMutation.isLoading,
        isSelecting: selectDocumentMutation.isLoading,
        isEditing: editDocumentMutation.isLoading,
        isExtracting: extractDocumentsMutation.isLoading,
        isExtractingAll: extractAllSelectedMutation.isLoading,
        isSearchingDocuments: searchDocumentsMutation.isLoading,
        isLoadingParsingTask: Boolean(parsingTaskId) && isLoadingParsingTask,
        isLoadingExtractionTask:
            Boolean(extractionTaskId) && isLoadingExtractionTask,

        // Error states
        error,
        uploadError,
        searchError: searchDocumentsMutation.error,
        parsingTaskError: parsingTaskError,
        extractionTaskError: extractionTaskError,

        // Upload progress
        uploadProgress,
        extractionTaskStatus: extractionTaskStatus?.data?.task_status,

        // Task progress
        parsingTaskProgress: parsingTaskStatus?.data,
        extractionTaskProgress: extractionTaskStatus?.data,
        parsingTaskFailureMessage: parsingTaskFailureMessage,
        parsingTaskHasFailed: parsingTaskHasFailed,
        extractionTaskFailureMessage: extractionTaskFailureMessage,
        extractionTaskHasFailed: extractionTaskHasFailed,

        // Actions
        refetch,
        uploadDocuments,
        pairDocument: pairDocumentMutation.mutate,
        unpairDocument: unpairDocumentMutation.mutate,
        selectDocument: selectDocumentMutation.mutate,
        editDocument: editDocumentMutation.mutate,
        extractDocuments: extractDocumentsMutation.mutate,
        extractAllSelected: extractAllSelectedMutation.mutate,
        searchForDocuments: searchDocumentsMutation.mutate,
        searchedDocuments: searchDocumentsMutation.data?.data ?? [],
        beginParse: beginParseMutation.mutate,
        beginExtraction: beginExtractionMutation.mutate,
    }
}
